import {
  ActionIcon,
  type ComboboxItem,
  Divider,
  Group,
  Input,
  SegmentedControl,
  SimpleGrid,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";
import { IconPencil, IconTrash } from "@tabler/icons-react";
import { type ChartTypeRegistry } from "chart.js";
import { useState } from "react";
import { MultiSelect, Select } from "src/components/shared/MantineCombobox";
import {
  type IChart,
  type TChartValueSource,
} from "src/redux/slices/entriesGeneratorSlice";

import {
  buildChartModel,
  getDefaultChartTitle,
  isChartNumeric,
  MAX_PIE_SLICES,
  resolveValueSource,
  truncateText,
} from "../Charts/Chart.utils";

const NO_SORT_VALUE = "__none__";
const ENTRIES_OPTION_VALUE = "__entries__";
const COUNT_OPTION_VALUE = "__count__";
const SUM_OPTION_PREFIX = "sum:";
const BAR_HINT_THRESHOLD = 15;
const CHART_TYPES = [
  { label: "Line", value: "line" },
  { label: "Bar", value: "bar" },
  { label: "Pie", value: "pie" },
];

const toOptionValue = (source: TChartValueSource) =>
  source.kind === "sum"
    ? `${SUM_OPTION_PREFIX}${source.column}`
    : source.kind === "count"
      ? COUNT_OPTION_VALUE
      : ENTRIES_OPTION_VALUE;

const fromOptionValue = (value: string | null): TChartValueSource =>
  value?.startsWith(SUM_OPTION_PREFIX)
    ? { kind: "sum", column: value.slice(SUM_OPTION_PREFIX.length) }
    : value === COUNT_OPTION_VALUE
      ? { kind: "count" }
      : { kind: "entries" };

const getValueSourceOptions = (chart: IChart, charts: IChart[]) => [
  ...(chart.type !== "pie"
    ? [
        {
          label: "Individual entries, in date order",
          value: ENTRIES_OPTION_VALUE,
        },
      ]
    : []),
  {
    label: `Count: number of entries per ${chart.name}`,
    value: COUNT_OPTION_VALUE,
  },
  ...[
    ...new Set(
      charts
        .filter(
          candidate =>
            candidate.name !== chart.name &&
            candidate.sectionIndex === chart.sectionIndex &&
            isChartNumeric(candidate),
        )
        .map(candidate => candidate.name),
    ),
  ].map(name => ({
    label: `Sum: total ${name} per ${chart.name}`,
    value: `${SUM_OPTION_PREFIX}${name}`,
  })),
];

const getFilterColumns = (chart: IChart, charts: IChart[]) => [
  ...new Set(
    charts
      .filter(
        candidate =>
          candidate.sectionIndex === chart.sectionIndex &&
          !isChartNumeric(candidate),
      )
      .map(candidate => candidate.name),
  ),
];

const getFilterColumn = (chart: IChart) => chart.filter?.column ?? null;

const getFilterOptions = (source: IChart | undefined) =>
  [
    ...new Set(
      (source?.rawVals ?? [])
        .filter(val => val !== undefined && val !== "")
        .map(String),
    ),
  ].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

const countCategories = (chart: IChart) =>
  buildChartModel({ ...chart, groupSmall: false }).labels.length;

interface Props {
  chart: IChart;
  // All columns of the logbook, value-only ones included.
  charts: IChart[];
  descriptionsByList: Record<string, Record<string, string>>;
  onChange: (updater: (chart: IChart) => IChart) => void;
  onRemove?: () => void;
}

const ChartSettingsForm = ({
  chart,
  charts,
  descriptionsByList,
  onChange,
  onRemove,
}: Props) => {
  const [renaming, setRenaming] = useState(false);
  const valueSource = resolveValueSource(chart);
  const isPie = chart.type === "pie";
  const categories = isPie ? countCategories(chart) : 0;
  const filterColumns = getFilterColumns(chart, charts);
  const filterColumn = getFilterColumn(chart);
  const filterSource = charts.find(
    candidate =>
      candidate.name === filterColumn &&
      candidate.sectionIndex === chart.sectionIndex,
  );

  const filterDescriptions = filterSource?.il
    ? descriptionsByList[filterSource.il]
    : undefined;
  // The picked codes show as short pills; the dropdown spells them out.
  const describeCode = (code: string) =>
    filterDescriptions?.[code]
      ? truncateText(`${code} - ${filterDescriptions[code]}`, 60)
      : code;

  const handleType = (value: string) => {
    onChange(prev => {
      const type = value as keyof ChartTypeRegistry;
      const next: IChart = { ...prev, type, trendline: false };
      if (
        type !== "pie" &&
        prev.valueSource?.kind !== "sum" &&
        !isChartNumeric(prev)
      ) {
        next.valueSource = { kind: "count" };
      }
      return next;
    });
  };

  return (
    <div>
      <Divider
        my={12}
        labelPosition="left"
        label={
          renaming ? (
            <TextInput
              autoFocus
              size="xs"
              w={320}
              aria-label="Chart title"
              placeholder={getDefaultChartTitle(chart)}
              value={chart.title ?? ""}
              onChange={event => {
                const title = event.currentTarget.value;
                onChange(prev => ({ ...prev, title: title || undefined }));
              }}
              onBlur={() => setRenaming(false)}
              onKeyDown={event => {
                if (event.key === "Enter" || event.key === "Escape")
                  setRenaming(false);
              }}
            />
          ) : (
            <Group gap={4} wrap="nowrap">
              <Text size="xs">
                {chart.title?.trim() || getDefaultChartTitle(chart)}
              </Text>
              <ActionIcon
                size="xs"
                variant="subtle"
                aria-label="Rename chart"
                onClick={() => setRenaming(true)}>
                <IconPencil size={12} />
              </ActionIcon>
            </Group>
          )
        }
      />
      <Group justify="space-between" align="flex-end" wrap="nowrap" mb={12}>
        <Input.Wrapper label="Chart type">
          <SegmentedControl
            display="flex"
            data={CHART_TYPES}
            value={chart.type}
            onChange={handleType}
          />
        </Input.Wrapper>
        {onRemove && (
          <ActionIcon
            size="lg"
            variant="subtle"
            color="red"
            aria-label={`Remove this chart for ${chart.name}`}
            onClick={onRemove}>
            <IconTrash size={20} />
          </ActionIcon>
        )}
      </Group>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12} verticalSpacing={12}>
        <Select
          label="Value"
          comboboxProps={{ withinPortal: true }}
          allowDeselect={false}
          data={getValueSourceOptions(chart, charts)}
          value={toOptionValue(valueSource)}
          onChange={(value: string | null) =>
            onChange(prev => ({
              ...prev,
              valueSource: fromOptionValue(value),
              trendline: prev.type !== "pie" ? prev.trendline : false,
            }))
          }
        />
        {valueSource.kind !== "entries" && (
          <Group gap={12} grow align="flex-end">
            <Select
              label="Sort by"
              comboboxProps={{ withinPortal: true }}
              allowDeselect={false}
              data={[
                { label: "Original order", value: NO_SORT_VALUE },
                { label: "Category", value: "label" },
                { label: "Value", value: "value" },
              ]}
              value={chart.sortBy ?? NO_SORT_VALUE}
              onChange={(value: string | null) =>
                onChange(prev => ({
                  ...prev,
                  sortBy:
                    value && value !== NO_SORT_VALUE
                      ? (value as "label" | "value")
                      : undefined,
                }))
              }
            />
            {chart.sortBy && (
              <Select
                label="Direction"
                comboboxProps={{ withinPortal: true }}
                allowDeselect={false}
                data={[
                  { label: "Ascending", value: "asc" },
                  { label: "Descending", value: "desc" },
                ]}
                value={chart.sortDirection ?? "asc"}
                onChange={(value: string | null) =>
                  onChange(prev => ({
                    ...prev,
                    sortDirection: value === "desc" ? "desc" : "asc",
                  }))
                }
              />
            )}
          </Group>
        )}
        {filterColumns.length > 0 && (
          <>
            <Select
              label="Filter by"
              clearable
              placeholder="No filter"
              comboboxProps={{ withinPortal: true }}
              data={filterColumns}
              value={filterColumn}
              onChange={(column: string | null) =>
                onChange(prev => ({
                  ...prev,
                  filter: column ? { column, values: [] } : undefined,
                }))
              }
            />
            {filterColumn && (
              <MultiSelect
                label={`Show only these (${filterColumn})`}
                searchable
                clearable
                placeholder={chart.filter?.values.length ? "" : "All"}
                comboboxProps={{ withinPortal: true }}
                data={getFilterOptions(filterSource)}
                renderOption={({ option }) => describeCode(option.value)}
                filter={({ options, search }) =>
                  (options as ComboboxItem[]).filter(option =>
                    describeCode(option.value)
                      .toLowerCase()
                      .includes(search.toLowerCase().trim()),
                  )
                }
                styles={{
                  pillsList: { maxHeight: 60, overflowY: "auto" },
                  // The search field's default min width forces a second row.
                  inputField: { minWidth: 24 },
                }}
                value={chart.filter?.values ?? []}
                onChange={(values: string[]) =>
                  onChange(prev => ({
                    ...prev,
                    filter: { column: filterColumn, values },
                  }))
                }
              />
            )}
          </>
        )}
      </SimpleGrid>
      {(!isPie || categories > MAX_PIE_SLICES) && (
        <Group gap={24} mt={12}>
          {!isPie && (
            <Switch
              checked={chart.trendline}
              onChange={event => {
                const { checked } = event.currentTarget;
                onChange(prev => ({ ...prev, trendline: checked }));
              }}
              label="Trendline"
            />
          )}
          {categories > MAX_PIE_SLICES && (
            <Switch
              checked={chart.groupSmall !== false}
              onChange={event => {
                const { checked } = event.currentTarget;
                onChange(prev => ({ ...prev, groupSmall: checked }));
              }}
              label='Group small slices as "Other"'
            />
          )}
        </Group>
      )}
      {categories > BAR_HINT_THRESHOLD && (
        <Text size="xs" c="dimmed" mt={8}>
          {categories} categories: a bar chart sorted by value may be easier to
          read than a pie.
        </Text>
      )}
    </div>
  );
};

export default ChartSettingsForm;
